import { apiClient } from "@/lib/api";
import { createApiRequest } from "@/lib/api/createApiService";
import { ESG_ENDPOINTS } from "./esg.endpoints";

const sanitizeFileSegment = (value) =>
  String(value || "")
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

const resolveMonthToken = (value) => {
  const text = String(value || "").trim();
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(text)
    ? text
    : new Date().toISOString().slice(0, 7);
};

const normalizeExportExtension = (extension) => {
  const normalized = String(extension || "")
    .replace(/^\./, "")
    .toLowerCase();
  if (normalized === "excel") return "xlsx";
  if (normalized === "word") return "docx";
  return normalized || "xlsx";
};

const buildPreviewReportExportFileName = (report = {}, extension = "xlsx") => {
  const reportName = sanitizeFileSegment(report?.name || "preview_report");
  const monthToken = resolveMonthToken(report?.endDate || report?.startDate);
  return `ESG_Reporting_${reportName}_${monthToken}.${normalizeExportExtension(extension)}`;
};

const extractFileName = (headers = {}) => {
  const contentDisposition =
    headers["content-disposition"] || headers["Content-Disposition"] || "";
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const basicMatch = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
  return basicMatch?.[1] || "";
};

export const reportPreviewService = {
  fetchPreview: async (payload = {}, options = {}) => {
    return createApiRequest({
      url: ESG_ENDPOINTS.ESG_REPORT_PREVIEW_QUERY,
      method: "POST",
      body: payload,
      ...options,
    });
  },

  getFilterOptions: async (params = {}, options = {}) => {
    return createApiRequest({
      url: ESG_ENDPOINTS.ESG_REPORT_PREVIEW_FILTER_OPTIONS,
      method: "GET",
      body: params,
      ...options,
    });
  },

  listReports: async (params = {}, options = {}) => {
    return createApiRequest({
      url: ESG_ENDPOINTS.ESG_REPORT_PREVIEW_LIST,
      method: "GET",
      body: params,
      ...options,
    });
  },

  createReport: async (payload = {}, options = {}) => {
    return createApiRequest({
      url: ESG_ENDPOINTS.ESG_REPORT_PREVIEW(),
      method: "POST",
      body: payload,
      ...options,
    });
  },

  updateReport: async (reportId, payload = {}, options = {}) => {
    if (!reportId) throw new Error("reportId is required");
    return createApiRequest({
      url: ESG_ENDPOINTS.ESG_REPORT_PREVIEW(reportId),
      method: "PATCH",
      body: payload,
      ...options,
    });
  },

  deleteReport: async (reportId, options = {}) => {
    if (!reportId) throw new Error("reportId is required");
    return createApiRequest({
      url: ESG_ENDPOINTS.ESG_REPORT_PREVIEW(reportId),
      method: "DELETE",
      ...options,
    });
  },

  exportReport: async (reportId, payload = {}, options = {}) => {
    if (!reportId) throw new Error("reportId is required");
    const response = await apiClient.post(
      ESG_ENDPOINTS.ESG_REPORT_PREVIEW_EXPORT(reportId),
      payload,
      {
        responseType: "blob",
        ...options,
      },
    );

    return {
      blob: response?.data,
      fileName: extractFileName(response?.headers || {}),
      contentType: response?.headers?.["content-type"] || "",
      status: response?.status,
    };
  },

  downloadExport: async (reportId, payload = {}, options = {}) => {
    const result = await reportPreviewService.exportReport(
      reportId,
      payload,
      options,
    );
    if (!result?.blob) return null;

    const url = URL.createObjectURL(result.blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download =
      result.fileName ||
      buildPreviewReportExportFileName(
        options.report,
        payload?.format || "xlsx",
      );
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);

    return result;
  },

  fetchReport: async (reportId, options = {}) => {
    if (!reportId) throw new Error("reportId is required");
    return createApiRequest({
      url: ESG_ENDPOINTS.ESG_REPORT_PREVIEW(reportId),
      method: "GET",
      ...options,
    });
  },

  completeReport: async (reportId, options = {}) => {
    if (!reportId) throw new Error("reportId is required");
    return createApiRequest({
      url: ESG_ENDPOINTS.ESG_REPORT_PREVIEW_COMPLETE(reportId),
      method: "POST",
      body: {},
      ...options,
    });
  },
};
